/**
 * Brand Service
 * API calls to Brands Lambda
 */

import { apiConfig } from '../config/api';
import { Brand } from '../types';
import { mockBrands } from '../data/mockData';
import { AuthService } from './authService';

export interface MaturityStep {
  key: string;
  title: string;
  description: string;
  completed: boolean;
  actionUrl?: string;
  currentValue?: string;
}

export interface BrandMaturity {
  percentage: number;
  completedSteps: number;
  totalSteps: number;
  steps: MaturityStep[];
}

export interface CreateBrandManagerPayload {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role?: 'manager' | 'agency';
}

// Debug: Log the actual API URL being used
console.log('🔍 BrandService BASE_URL:', apiConfig.brandsApi);
console.log('🔍 BrandService apiConfig:', apiConfig);

const BASE_URL = apiConfig.brandsApi;

export class BrandService {
  /**
   * Get auth headers with dashboard token.
   */
  private static async getHeaders(): Promise<HeadersInit> {
    const token = await AuthService.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get all brands
   */
  static async getAllBrands(): Promise<Brand[]> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/brands`);
      const data = await response.json();
      
      console.log('🔍 API Response (All Brands):', data);
      
      if (!data.success || !data.data || !data.data.brands) {
        throw new Error('Invalid API response');
      }
      
      // Map API response to Brand type
      const brands: Brand[] = data.data.brands.map((apiBrand: any) => ({
        id: apiBrand.id,
        name: apiBrand.name,
        description: apiBrand.description,
        logoUrl: apiBrand.logoUrl,
        website: apiBrand.websiteUrl,
        xmlFeedUrl: apiBrand.xmlUrl,
        apiKey: apiBrand.apiKey || undefined,
        isActive: apiBrand.isActive,
        allowedDomains: apiBrand.allowedDomains || [],
        totalProducts: apiBrand.totalProducts || 0,
        totalMappings: apiBrand.totalMappings || 0,
        createdAt: apiBrand.createdAt || apiBrand.creationDate,
        updatedAt: apiBrand.updatedAt,
        createdBy: apiBrand.createdBy,
      }));
      
      console.log('✅ Mapped Brands:', brands);
      return brands;
    } catch (error) {
      console.error('Failed to fetch brands:', error);
      // Return mock data as fallback
      return mockBrands;
    }
  }

  /**
   * Get brand by ID
   */
  static async getBrandById(id: string): Promise<Brand> {
    try {
      const url = `${BASE_URL}/api/v1/brands/${id}`;
      console.log('🔍 BrandService.getBrandById - Full URL:', url);
      console.log('🔍 BrandService.getBrandById - BASE_URL:', BASE_URL);
      const response = await fetch(url);
      const data = await response.json();
      
      console.log('🔍 API Response:', data);
      
      if (!data.success || !data.data || !data.data.brand) {
        throw new Error('Invalid API response');
      }
      
      // Map API response to Brand type
      const apiBrand = data.data.brand;
      const brand: Brand = {
        id: apiBrand.id,
        name: apiBrand.name,
        description: apiBrand.description,
        logoUrl: apiBrand.logoUrl,
        website: apiBrand.websiteUrl,
        xmlFeedUrl: apiBrand.xmlUrl,
        apiKey: apiBrand.apiKey || undefined,
        isActive: apiBrand.isActive,
        allowedDomains: apiBrand.allowedDomains || [],
        totalProducts: apiBrand.totalProducts || 0,
        totalMappings: apiBrand.totalMappings || 0,
        createdAt: apiBrand.createdAt || apiBrand.creationDate,
        updatedAt: apiBrand.updatedAt,
        createdBy: apiBrand.createdBy,
      };
      
      console.log('✅ Mapped Brand:', brand);
      return brand;
    } catch (error) {
      console.error('Failed to fetch brand:', error);
      console.error('Failed URL was:', `${BASE_URL}/api/v1/brands/${id}`);
      // Return mock data as fallback
      const brand = mockBrands.find(b => b.id === id);
      if (!brand) throw new Error('Brand not found');
      return brand;
    }
  }

  /**
   * Create new brand
   */
  static async createBrand(
    brandData: Omit<Brand, 'id' | 'createdAt' | 'updatedAt'> & { assignedEmails?: string[] }
  ): Promise<Brand> {
    try {
      // Map Brand type to API format
      const { assignedEmails, ...brandFields } = brandData;
      const apiData = {
        name: brandFields.name,
        description: brandFields.description,
        logoUrl: brandFields.logoUrl,
        websiteUrl: brandFields.website,
        xmlUrl: brandFields.xmlFeedUrl,
        isActive: brandFields.isActive,
        ...(assignedEmails && assignedEmails.length > 0 && { assignedEmails }),
      };

      const response = await fetch(`${BASE_URL}/api/v1/brands`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.brand) {
        throw new Error('Invalid API response');
      }
      
      // Map response back to Brand type
      const apiBrand = data.data.brand;
      return {
        id: apiBrand.id,
        name: apiBrand.name,
        description: apiBrand.description,
        logoUrl: apiBrand.logoUrl,
        website: apiBrand.websiteUrl,
        xmlFeedUrl: apiBrand.xmlUrl,
        apiKey: apiBrand.apiKey,
        isActive: apiBrand.isActive,
        allowedDomains: apiBrand.allowedDomains || [],
        totalProducts: apiBrand.totalProducts || 0,
        totalMappings: apiBrand.totalMappings || 0,
        createdAt: apiBrand.createdAt || apiBrand.creationDate,
        updatedAt: apiBrand.updatedAt,
        createdBy: apiBrand.createdBy,
      };
    } catch (error) {
      console.error('Failed to create brand:', error);
      throw error;
    }
  }

  /**
   * Update brand
   */
  static async updateBrand(id: string, brandData: Partial<Brand>): Promise<Brand> {
    try {
      // Map Brand type to API format
      const apiData: any = {};
      if (brandData.name) apiData.name = brandData.name;
      if (brandData.description !== undefined) apiData.description = brandData.description;
      if (brandData.logoUrl !== undefined) apiData.logoUrl = brandData.logoUrl;
      if (brandData.website !== undefined) apiData.websiteUrl = brandData.website;
      if (brandData.xmlFeedUrl !== undefined) apiData.xmlUrl = brandData.xmlFeedUrl;
      if (brandData.isActive !== undefined) apiData.isActive = brandData.isActive;

      const response = await fetch(`${BASE_URL}/api/v1/brands/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });
      const data = await response.json();
      
      if (!data.success || !data.data || !data.data.brand) {
        throw new Error('Invalid API response');
      }
      
      // Map response back to Brand type
      const apiBrand = data.data.brand;
      return {
        id: apiBrand.id,
        name: apiBrand.name,
        description: apiBrand.description,
        logoUrl: apiBrand.logoUrl,
        website: apiBrand.websiteUrl,
        xmlFeedUrl: apiBrand.xmlUrl,
        apiKey: apiBrand.apiKey,
        isActive: apiBrand.isActive,
        allowedDomains: apiBrand.allowedDomains || [],
        totalProducts: apiBrand.totalProducts || 0,
        totalMappings: apiBrand.totalMappings || 0,
        createdAt: apiBrand.createdAt || apiBrand.creationDate,
        updatedAt: apiBrand.updatedAt,
        createdBy: apiBrand.createdBy,
      };
    } catch (error) {
      console.error('Failed to update brand:', error);
      throw error;
    }
  }

  /**
   * Delete brand
   */
  static async deleteBrand(id: string): Promise<void> {
    try {
      await fetch(`${BASE_URL}/api/v1/brands/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error('Failed to delete brand:', error);
      throw error;
    }
  }

  /**
   * Create manager user for a brand
   */
  static async createBrandManager(brandId: string, payload: CreateBrandManagerPayload): Promise<{
    id: string;
    email: string;
    name: string;
    role: 'manager' | 'agency';
    brandId: string;
  }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${BASE_URL}/api/v1/brands/${brandId}/users`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    if (!response.ok || !data.success || !data.data?.user) {
      throw new Error(data.error?.message || data.error || data.message || 'Kullanıcı oluşturulamadı');
    }

    return data.data.user;
  }

  /**
   * Get brand statistics
   */
  static async getBrandStats(brandId: string): Promise<any> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/brands/${brandId}/stats`);
      const data = await response.json();
      return data.stats;
    } catch (error) {
      console.error('Failed to fetch brand stats:', error);
      // Return mock data as fallback
      return {
        totalProducts: 156,
        activeIntegrations: 3,
        totalViews: 2847,
        conversionRate: 3.48,
      };
    }
  }

  /**
   * Generate API Key for brand
   */
  static async generateApiKey(
    brandId: string, 
    allowedDomains: string[] = [], 
    monthlyLimit: number = 10000
  ): Promise<{ apiKey: string; apiSecret?: string; expiresAt: string; allowedDomains: string[]; monthlyLimit: number }> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/brands/${brandId}/api-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          allowedDomains,
          monthlyLimit,
        }),
      });
      
      const data = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error(data.message || 'API key üretilemedi');
      }
      
      return data.data;
    } catch (error) {
      console.error('Failed to generate API key:', error);
      throw error;
    }
  }

  /**
   * Get brand maturity/completeness score
   */
  static async getBrandMaturity(brandId: string): Promise<BrandMaturity> {
    try {
      const response = await fetch(`${BASE_URL}/api/v1/brands/${brandId}/maturity`);
      const data = await response.json();
      
      if (!data.success || !data.data) {
        throw new Error('Invalid API response');
      }
      
      return data.data;
    } catch (error) {
      console.error('Failed to fetch brand maturity:', error);
      // Return default maturity as fallback
      return {
        percentage: 0,
        completedSteps: 0,
        totalSteps: 4,
        steps: [
          { key: 'website_url', title: 'Website URL', description: 'Markanızın web sitesi adresini ekleyin', completed: false, actionUrl: '/brand-profile' },
          { key: 'xml_url', title: 'XML Feed URL', description: 'Ürün kataloğunuzun XML feed adresini ekleyin', completed: false, actionUrl: '/brand-profile' },
          { key: 'xml_mapping', title: 'XML Mapping', description: 'XML alanlarını sistem alanlarıyla eşleştirin', completed: false, actionUrl: '/products?showMapping=true' },
          { key: 'products', title: 'Ürünler', description: 'En az 1 ürün sisteme yüklenmiş olmalı', completed: false, actionUrl: '/products' },
        ]
      };
    }
  }
}
